import { AxiosResponse } from "axios";
import {
  AltiusRes,
  ICreateInvestorData,
  IInvestor,
  InvestorWithBank,
  ISearchInvestorData,
  ISearchInvestorResponse,
  UpdateInvestorDto
} from "store/types";
import { toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";
import { toastify } from "utils/tyostify";

import { DealAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IInvestorActions {
  getInvestorsAction: () => Promise<void>;
  getInvestorsListAction: () => Promise<void>;
  createInvestorAction: (data: ICreateInvestorData) => Promise<IInvestor>;
  updateInvestorAction: (data: UpdateInvestorDto) => Promise<IInvestor>;
  uploadInvestorRelationsAction: (file: File) => Promise<void>;
  uploadSignatureImageAction: (investorId: number, signatureIndex: number, image: File) => Promise<string>;
  getBankSplitAction: (
    investorId: number,
    dealId: number
  ) => Promise<Array<{ bank_detail_id?: number; account_number?: string; percentage: number }> | null>;
  deleteInvestorAction: (id: number) => Promise<void>;
  searchInvestorsAction: (data: ISearchInvestorData) => Promise<IInvestor[]>;
  checkIsInvestorDeletableAction: (data: { dealId: number; roundId: number; investorId: number }) => Promise<void>;
  getAvailableInvestorsList: (data: {
    deal_id: number | undefined;
    round_id: number | undefined;
  }) => Promise<Pick<IInvestor, "id" | "name">[]>;
  inviteInvestorAction: (id: number) => Promise<void>;
  getDealInvestors: (data: {
    dealId: number;
    with_all_investors?: boolean;
  }) => Promise<
    { email: string | null; id: number; name: string; phone_number: string | null; is_splittable: boolean }[]
  >;
}
export class InvestorActions extends AsyncActions implements IInvestorActions {
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public getBankSplitAction = async (investorId: number, dealId: number) => {
    try {
      const {
        data: { data }
      } = await this.api.get<
        AltiusRes<Array<{ bank_detail_id?: number; account_number?: string; percentage: number }> | null>
      >({
        url: `/api/v0.0.3/investors/${investorId}/bank-split/${dealId}`
      });
      return data ?? null;
    } catch {
      return null;
    }
  };

  public uploadSignatureImageAction = async (
    investorId: number,
    signatureIndex: number,
    image: File
  ): Promise<string> => {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("signature_index", String(signatureIndex));
    const {
      data: { data }
    } = await this.api.post<AltiusRes<{ signature_image_url: string }>>({
      url: `/api/v0.0.3/investors/${investorId}/signature-image`,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return data.signature_image_url;
  };

  public uploadInvestorRelationsAction = async (file: File): Promise<void> => {
    await this.api.post({
      url: "/api/v0.0.3/investors/relations/upload",
      data: toFormData({ file }),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    toastify({ message: "Investor relations has been uploaded" });
  };

  public getInvestorsAction = async (): Promise<void> => {
    const {
      data: { data: payload }
    }: AxiosResponse<{ data: InvestorWithBank[] }> = await this.api.get({
      url: "/api/v0.0.3/investors"
    });
    this.dispatch({ type: DealAction.GetInvestorsSuccess, payload });
  };

  public getInvestorsListAction = async (): Promise<void> => {
    return this.api
      .get({
        url: "/api/v0.0.3/investors/list/download",
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public createInvestorAction = async (data: ICreateInvestorData): Promise<IInvestor> => {
    const {
      status,
      data: { data: payload }
    }: AxiosResponse<{ data: IInvestor }> = await this.api.post({
      url: "/api/v0.0.3/investors",
      data: data
    });
    if (status === 202) {
      toastify({
        message: "An investor with this name has been defined and archived, the system will reactive the investor."
      });
    }
    this.dispatch({ type: DealAction.CreateInvestorSuccess, payload });
    return payload;
  };

  public updateInvestorAction: IInvestorActions["updateInvestorAction"] = async (data) => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<InvestorWithBank>>({
      url: `/api/v0.0.3/investors/${data.id}`,
      data: data
    });
    this.dispatch({ type: DealAction.UpdateInvestorSuccess, payload });
    return payload;
  };

  public deleteInvestorAction = async (id: number): Promise<void> => {
    await this.api.delete({
      url: `/api/v0.0.3/investors/${id}`
    });
    this.dispatch({ type: DealAction.DeleteInvestorSuccess, payload: id });
  };

  public searchInvestorsAction = async (data: ISearchInvestorData): Promise<IInvestor[]> => {
    const { name, roundId } = data;
    const body: { name: string; round_id?: number } = { name };

    if (roundId) {
      body.round_id = roundId;
    }
    const {
      data: { data: responseData }
    }: ISearchInvestorResponse = await this.api.post({
      url: "/api/v0.0.3/investors/search",
      data: body
    });
    return responseData.investors;
  };

  public checkIsInvestorDeletableAction: IInvestorActions["checkIsInvestorDeletableAction"] = async ({
    dealId,
    roundId,
    investorId
  }) => {
    await this.api.get({ url: `/api/v0.0.3/deletable/deals/${dealId}/rounds/${roundId}/investors/${investorId}` });
  };

  // return available investors list for deal and round
  public getAvailableInvestorsList = async (params: {
    deal_id?: number;
    round_id?: number;
  }): Promise<Pick<IInvestor, "id" | "name">[]> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Pick<IInvestor, "id" | "name">[]>>({
      url: "/api/v0.0.3/reports/deal-quick-view/investors",
      params
    });
    return payload;
  };

  public inviteInvestorAction = async (id: number): Promise<void> => {
    await this.api.post({
      url: `/api/v0.0.3/investors/${id}/invite-report`
    });
  };

  public getDealInvestors = async ({
    dealId,
    with_all_investors
  }: {
    dealId: number;
    with_all_investors?: boolean;
  }) => {
    const {
      data: { data }
    } = await this.api.post({
      url: "/api/v0.0.3/investors/search",
      data: {
        deal_id: dealId,
        ...(with_all_investors && { with_all_investors: true })
      }
    });
    return data.investors;
  };
}
